import { DeleteOutlined, EditOutlined, MessageOutlined, MoreOutlined, PushpinFilled, PushpinOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, List, Mentions, Space, Tooltip, Typography } from "antd";
import AddCommentForm from "components/molecules/AddCommentForm";
import moment from "moment";
import { useMemo, useState } from "react";

const { Text, Paragraph } = Typography;

const ReplyContext = ({ parent }) => (
    <a href={`#comment-${parent.commentId}`} style={{ textDecoration: "none", color: "inherit" }}>
        <div style={{ background: "#f5f5f5", padding: "8px 12px", borderLeft: "4px solid #d9d9d9", borderRadius: "4px", marginBottom: "8px" }}>
            <Text strong style={{ display: "block", fontSize: "12px" }}>
                {parent.createdByName}
            </Text>
            <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0, fontSize: "12px" }} type="secondary">
                {parent.content}
            </Paragraph>
        </div>
    </a>
);

const DeletedReplyContext = () => (
    <div style={{ background: "#fafafa", padding: "8px 12px", borderLeft: "4px solid #e8e8e8", borderRadius: "4px", marginBottom: "8px" }}>
        <Text italic type="secondary" style={{ fontSize: "12px" }}>
            The original comment was deleted.
        </Text>
    </div>
);

const CommentItem = ({ comment, parentComment, users = [], onUpdate, onDelete, onAddReply, permission }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isReplying, setIsReplying] = useState(false);
    const [editedContent, setEditedContent] = useState(comment.content);

    const activeUsersMap = useMemo(() => {
        return users
            .filter((user) => user.active && !user.deleted)
            .reduce((acc, user) => {
                const fullName = `${user.firstName} ${user.lastName}`;
                acc[fullName] = user.userId;
                return acc;
            }, {});
    }, [users]);

    const allKnownUsersMap = useMemo(() => {
        return users
            .filter((user) => !user.deleted)
            .reduce((acc, user) => {
                const fullName = `${user.firstName} ${user.lastName}`;
                acc[fullName] = user;
                return acc;
            }, {});
    }, [users]);

    const mentionOptions = useMemo(() => {
        return users
            .filter((user) => !user.deleted)
            .map((user) => {
                const fullName = `${user.firstName} ${user.lastName}`;
                return {
                    key: user.userId,
                    value: fullName,
                    disabled: !user.active,
                    label: (
                        <>
                            <span style={{ textDecoration: !user.active ? "line-through" : "none" }}>{fullName}</span>
                            {!user.active && <span style={{ color: "#8c8c8c", marginLeft: "8px" }}>(Inactive)</span>}
                        </>
                    ),
                };
            });
    }, [users]);

    const menuItems = useMemo(() => {
        const items = [];
        if (permission?.canAdd && !parentComment && !comment.isReply) {
            items.push({
                key: "reply",
                icon: <MessageOutlined />,
                label: "Reply",
            });
        }
        if (permission?.canEdit) {
            items.push({
                key: "edit",
                icon: <EditOutlined />,
                label: "Edit",
            });
            items.push({
                key: "pin",
                icon: comment.pinned ? <PushpinOutlined /> : <PushpinFilled />,
                label: comment.pinned ? "Unpin" : "Pin",
            });
        }
        if (permission?.canDelete) {
            items.push({
                key: "delete",
                icon: <DeleteOutlined />,
                label: "Delete",
                danger: true,
            });
        }
        return items;
    }, [permission, parentComment, comment.isReply, comment.pinned]);

    if (!permission?.canView) {
        return null;
    }

    const handleUpdate = () => {
        if (editedContent.trim()) {
            const taggedUserIds = new Set();
            for (const fullName in activeUsersMap) {
                const mentionRegex = new RegExp(`@${fullName}\\b`, "g");
                if (editedContent.match(mentionRegex)) {
                    taggedUserIds.add(activeUsersMap[fullName]);
                }
            }
            onUpdate(comment.commentId, { content: editedContent, taggedUsers: Array.from(taggedUserIds) });
            setIsEditing(false);
        }
    };

    const handleReplySubmit = (replyData) => {
        onAddReply(comment.commentId, replyData);
        setIsReplying(false);
    };

    const renderContentWithMentions = (content) => {
        if (!content || !users.length) return content;

        const userNames = Object.keys(allKnownUsersMap);
        if (userNames.length === 0) return content;

        const escapedUserNames = userNames.map((name) => name.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"));
        const regex = new RegExp(`@(${escapedUserNames.join("|")})\\b`, "g");

        const elements = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(content)) !== null) {
            if (match.index > lastIndex) {
                elements.push(content.substring(lastIndex, match.index));
            }

            const fullName = match[1];
            const user = allKnownUsersMap[fullName];

            elements.push(
                <span
                    key={match.index}
                    style={{
                        color: "#1890ff",
                        fontWeight: "bold",
                        textDecoration: !user.active ? "line-through" : "none",
                        opacity: !user.active ? 0.75 : 1,
                    }}
                >
                    @{fullName}
                </span>,
            );

            lastIndex = regex.lastIndex;
        }

        if (lastIndex < content.length) {
            elements.push(content.substring(lastIndex));
        }

        return elements;
    };

    const handleMenuClick = ({ key, domEvent }) => {
        domEvent.stopPropagation();
        if (key === "edit") {
            setIsEditing(true);
        } else if (key === "reply") {
            setIsReplying(!isReplying);
            setIsEditing(false);
        } else if (key === "pin") {
            onUpdate(comment.commentId, { pinned: !comment.pinned });
        } else if (key === "delete") {
            onDelete(comment.commentId);
        }
    };

    const hasAnyAction = menuItems.length > 0;

    const actions = hasAnyAction
        ? [
              <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={["click"]} key="actions">
                  <button
                      type="button"
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit", font: "inherit" }}
                      aria-label="More options"
                      onClick={(e) => e.stopPropagation()}
                  >
                      <MoreOutlined />
                  </button>
              </Dropdown>,
          ]
        : [];

    const title = (
        <Space align="center">
            <Text strong>{comment.createdByName}</Text>
            <Tooltip title={moment(comment.createdDate).format("llll")}>
                <Text type="secondary" style={{ fontSize: "12px", fontWeight: "normal" }}>
                    {moment(comment.createdDate).fromNow()}
                </Text>
            </Tooltip>
            {comment.pinned && <PushpinFilled style={{ color: "orange" }} />}
        </Space>
    );

    const description = (
        <>
            {comment.isReply && (parentComment ? <ReplyContext parent={parentComment} /> : <DeletedReplyContext />)}

            {isEditing ? (
                <>
                    <Mentions
                        className="comment-mentions-input"
                        rows={3}
                        value={editedContent}
                        onChange={setEditedContent}
                        style={{ marginTop: 8 }}
                        notFoundContent="No users found"
                        options={mentionOptions}
                    />
                    <Space style={{ marginTop: 8 }}>
                        <Button size="small" type="primary" onClick={handleUpdate} disabled={editedContent === comment.content || editedContent.trim() === ""}>
                            Save
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setIsEditing(false);
                                setEditedContent(comment.content);
                            }}
                        >
                            Cancel
                        </Button>
                    </Space>
                </>
            ) : (
                <Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>{renderContentWithMentions(comment.content)}</Paragraph>
            )}
        </>
    );

    return (
        <>
            <List.Item
                id={`comment-${comment.commentId}`}
                actions={hasAnyAction ? actions : undefined}
                style={{ background: "#fff", padding: "12px 0", alignItems: "flex-start", border: "none", borderBottom: "1px solid #f0f0f0" }}
            >
                <List.Item.Meta avatar={<Avatar>{comment.createdByName.charAt(0)}</Avatar>} title={title} description={description} />
            </List.Item>
            {isReplying && permission.canAdd && (
                <div style={{ padding: "12px 4px 0px 4px" }}>
                    <AddCommentForm users={users} onSubmit={handleReplySubmit} onCancel={() => setIsReplying(false)} submitText="Post Reply" placeholder="Write a reply..." />
                </div>
            )}
        </>
    );
};

export default CommentItem;
